import React, { useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { LIST_CUSTOM_ORDER_WEBHOOKS } from 'components/Integrations/Manage/ManageCustomWebhook/queries'
import { CustomOrderWebhook } from 'components/Integrations/Manage/ManageCustomWebhook/types'
import { useIntegration } from 'hooks/useGlobalContext/useIntegration'
import { LoaderProps } from './types'

// A merchant can legitimately have zero custom webhooks, so a length-based
// `skip` (useFetchOnce's default) can't tell "unfetched" from "empty" — see
// CLAUDE.md's useQuery vs useLazyQuery note. Triggered manually here instead,
// guarded by the dedicated customWebhooksLoaded flag.
export const CustomWebhooksLoader = ({
  isParentLoaded = true,
  children
}: LoaderProps) => {
  const { addMessage } = useMessageStore()
  const { customWebhooksLoaded, setCustomWebhooksLoaded, setCustomWebhooks } =
    useIntegration()

  const [fetchCustomWebhooks, { data, loading, error }] = useLazyQuery<{
    listCustomOrderWebhooks: CustomOrderWebhook[]
  }>(LIST_CUSTOM_ORDER_WEBHOOKS)

  useEffect(() => {
    if (!customWebhooksLoaded) {
      fetchCustomWebhooks()
    }
  }, [customWebhooksLoaded])

  useEffect(() => {
    if (error) {
      addMessage({ type: 'error', content: 'Failed to load custom webhooks' })
      // A failed fetch still counts as settled — otherwise the rest of the
      // page stays stuck behind LoaderSwitcher forever.
      setCustomWebhooksLoaded(true)
    }
  }, [error])

  useEffect(() => {
    if (data) {
      setCustomWebhooks(data.listCustomOrderWebhooks)
      setCustomWebhooksLoaded(true)
    }
  }, [data])

  const isLoaded = isParentLoaded && customWebhooksLoaded && !loading

  return <React.Fragment>{children(isLoaded)}</React.Fragment>
}
